import { Children, memo, useCallback, useState } from 'react';
import { Arrow, Gallery, NavDots } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import Link from '@Navigation/Link';
import CroppedText from './elems/CroppedText';
import logo from './icons/logo.svg';
import styles from './InstagramWidget.module.css';

import type { InstagramWidgetData } from '@Pages/PageWikiArticle';
import type { FC, HTMLAttributes } from 'react';

export interface InstagramWidgetProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  contentData: InstagramWidgetData;
}

const InstagramWidget: FC<InstagramWidgetProps> = (props) => {
  const { contentData, ...restProps } = props;
  const { isMobileM } = useMedias();
  const numberOfSlides = Children.count(contentData.images);
  const [slide, setSlide] = useState(0);

  const normalizeSlide = useCallback(
    (value: number) => {
      if (value < 0) return numberOfSlides - 1;
      if (value > numberOfSlides - 1) return 0;

      return value;
    },
    [numberOfSlides],
  );

  const handleChangeCurrent = useCallback(({ current }) => {
    setSlide(current);
  }, []);

  const handleChangeSlide = useCallback((_e, index) => {
    setSlide(index);
  }, []);

  const handlePrev = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide]);

  return (
    <div {...restProps}>
      <div className={styles.container}>
        {contentData.header && (
          <Link to={contentData.header.link} target='_blank' className={styles.header}>
            <Img className={styles.logo} src={logo} />
            {contentData.header.name}
          </Link>
        )}

        <div className={styles.gallery}>
          {numberOfSlides > 1 && !isMobileM && (
            <div>
              <Arrow position='left' className={styles.arrowPrev} onClick={handlePrev} />
              <Arrow position='right' className={styles.arrowNext} onClick={handleNext} />
            </div>
          )}
          {contentData.images?.length > 0 && (
            <Gallery slideIndex={slide} onChangeCurrent={handleChangeCurrent}>
              {contentData.images.map((image) => (
                <div key={image} className={styles.galleryItem}>
                  <Img src={image} className={styles.image} />
                </div>
              ))}
            </Gallery>
          )}
        </div>

        <div className={styles.footer}>
          <CroppedText className={styles.footerText} text={contentData.recordText}>
            {contentData.author && (
              <span className={styles.author}>{`${contentData.author} `}</span>
            )}
          </CroppedText>
        </div>
      </div>

      {numberOfSlides > 1 && (
        <div className={styles.wrapperDots}>
          <NavDots count={numberOfSlides} defaultIndex={slide} onChangeSlide={handleChangeSlide} />
        </div>
      )}
    </div>
  );
};

export default memo(InstagramWidget);
